import QRCode from 'qrcode';

export interface DeviceSize {
  id: string;
  label: string;
  platform: 'ios' | 'android';
  width: number;
  height: number;
}

/** Native screen resolutions in pixels (portrait). */
export const DEVICES: DeviceSize[] = [
  { id: 'iphone-17-pro-max', label: 'iPhone 17 Pro Max / 16 Pro Max', platform: 'ios', width: 1320, height: 2868 },
  { id: 'iphone-air', label: 'iPhone Air', platform: 'ios', width: 1260, height: 2736 },
  { id: 'iphone-17-pro', label: 'iPhone 17 / 17 Pro / 16 Pro', platform: 'ios', width: 1206, height: 2622 },
  { id: 'iphone-16-plus', label: 'iPhone 16 Plus / 15 Plus / 15 Pro Max / 14 Pro Max', platform: 'ios', width: 1290, height: 2796 },
  { id: 'iphone-16', label: 'iPhone 16 / 15 / 15 Pro / 14 Pro', platform: 'ios', width: 1179, height: 2556 },
  { id: 'iphone-14', label: 'iPhone 16e / 14 / 13 / 13 Pro / 12 / 12 Pro', platform: 'ios', width: 1170, height: 2532 },
  { id: 'iphone-13-mini', label: 'iPhone 13 mini / 12 mini', platform: 'ios', width: 1080, height: 2340 },
  { id: 'iphone-11-pro-max', label: 'iPhone 11 Pro Max / XS Max', platform: 'ios', width: 1242, height: 2688 },
  { id: 'iphone-11', label: 'iPhone 11 / XR', platform: 'ios', width: 828, height: 1792 },
  { id: 'iphone-se', label: 'iPhone SE (2./3. Gen.) / 8', platform: 'ios', width: 750, height: 1334 },
  { id: 'galaxy-s-ultra', label: 'Samsung Galaxy S25 Ultra / S24 Ultra / S25+ / S24+', platform: 'android', width: 1440, height: 3120 },
  { id: 'galaxy-s', label: 'Samsung Galaxy S25 / S24 / S23', platform: 'android', width: 1080, height: 2340 },
  { id: 'galaxy-a', label: 'Samsung Galaxy A-Serie', platform: 'android', width: 1080, height: 2408 },
  { id: 'pixel-9-pro-xl', label: 'Google Pixel 9 Pro XL', platform: 'android', width: 1344, height: 2992 },
  { id: 'pixel-9-pro', label: 'Google Pixel 9 Pro / 10 Pro', platform: 'android', width: 1280, height: 2856 },
  { id: 'pixel-9', label: 'Google Pixel 9 / 8 / 8 Pro', platform: 'android', width: 1080, height: 2400 },
  { id: 'android-fhd', label: 'Android Standard (Full HD+)', platform: 'android', width: 1080, height: 2400 },
  { id: 'android-qhd', label: 'Android Standard (QHD+)', platform: 'android', width: 1440, height: 3200 },
];

export const CUSTOM_DEVICE_ID = 'custom';

export interface WallpaperOptions {
  width: number;
  height: number;
  background: string;
  /** Data URL or object URL of the customer's logo (already loaded as image). */
  logo: HTMLImageElement | null;
  url: string;
  /** QR colour; defaults to a contrasting colour for the background. */
  qrColor?: string;
}

/** Relative luminance (0 dark … 1 light) of a #rrggbb colour. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 1;
  const n = parseInt(m[1]!, 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

/** Dark QR on light backgrounds, white QR on dark ones. */
export function autoQrColor(background: string): string {
  return luminance(background) > 0.4 ? '#111111' : '#ffffff';
}

/** Draws the QR modules (no background) centred in a square; integer module size keeps edges crisp. */
function drawQr(ctx: CanvasRenderingContext2D, url: string, x: number, y: number, size: number, color: string) {
  const qr = QRCode.create(url, { errorCorrectionLevel: 'M' });
  const n = qr.modules.size;
  const cell = Math.max(1, Math.floor(size / n));
  const off = Math.floor((size - cell * n) / 2);
  ctx.fillStyle = color;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.modules.get(c, r)) ctx.fillRect(x + off + c * cell, y + off + r * cell, cell, cell);
    }
  }
}

/** Renders the wallpaper into the given canvas. Layout leaves the lock-screen clock (top) and shortcuts (bottom) free. */
export function renderWallpaper(canvas: HTMLCanvasElement, o: WallpaperOptions) {
  const { width: W, height: H } = o;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = o.background;
  ctx.fillRect(0, 0, W, H);

  const color = o.qrColor ?? autoQrColor(o.background);
  const qrSize = Math.round(Math.min(W * 0.5, H * 0.26));
  const qrX = Math.round((W - qrSize) / 2);
  const qrY = Math.round(H * 0.68 - qrSize / 2);
  drawQr(ctx, o.url, qrX, qrY, qrSize, color);

  if (o.logo && o.logo.naturalWidth > 0) {
    const maxW = W * 0.52;
    const maxH = H * 0.1;
    const ratio = Math.min(maxW / o.logo.naturalWidth, maxH / o.logo.naturalHeight);
    const lw = o.logo.naturalWidth * ratio;
    const lh = o.logo.naturalHeight * ratio;
    ctx.drawImage(o.logo, (W - lw) / 2, qrY - lh - H * 0.035, lw, lh);
  }
}

export async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/png' | 'image/jpeg'): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Export failed'))), type, 0.95);
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export type QrFormat = 'png' | 'jpg' | 'svg';

/** Exports only the QR code. PNG/SVG are transparent; JPG has no alpha, so it gets a white background. */
export async function exportQr(url: string, format: QrFormat, size: number, color: string): Promise<Blob> {
  if (format === 'svg') {
    const svg = await QRCode.toString(url, {
      type: 'svg',
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: color, light: '#0000' },
    });
    return new Blob([svg], { type: 'image/svg+xml' });
  }
  const qr = QRCode.create(url, { errorCorrectionLevel: 'M' });
  const margin = 1;
  const cells = qr.modules.size + margin * 2;
  const cell = Math.max(1, Math.round(size / cells));
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = cell * cells;
  const ctx = canvas.getContext('2d')!;
  if (format === 'jpg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.fillStyle = format === 'jpg' && luminance(color) > 0.4 ? '#111111' : color;
  for (let r = 0; r < qr.modules.size; r++) {
    for (let c = 0; c < qr.modules.size; c++) {
      if (qr.modules.get(c, r)) ctx.fillRect((c + margin) * cell, (r + margin) * cell, cell, cell);
    }
  }
  return canvasToBlob(canvas, format === 'jpg' ? 'image/jpeg' : 'image/png');
}
